// "Animar imagen" (las DOS ramas del toggle multi-escena, ADR-579/582) +
// prompt sugerido (ADR-579) + video desde PLANTILLA (ADR-581) — migrados de
// n8n al atlas-backend. `/v1/videos/from-image` decide por
// `multiples_escenas`: true → Kling multi-shot (cobra segundos × 2); false →
// Grok (segundos × 1). OJO alcance: el planificador cinematográfico y el
// video personalizado NO pasan por acá — generan multi-grupo real que atlas
// todavía no soporta y siguen contra `webhook/video_cinema` de n8n.
//
// Flag de rollback (VITE_USE_ATLAS_BACKEND):
//  • ON  → POST /v1/videos/from-image y /v1/videos/prompt con JWT de Supabase
//          (sin user_id; el backend lo deriva del token). El body de escena
//          única viaja tal cual (el backend lee scenes[0]) + `execution_id`
//          (obligatorio, contrato de polling).
//  • OFF → los webhooks de n8n de siempre, con GENERATE_AUTH_TOKEN + user_id.
//
// Respuestas del backend nuevo (diferencias vs n8n):
//  • 200 {response:'success', video_id} → idéntico a n8n.
//  • 402 {error_type:'billing'} → sin créditos (GenerateAdError accionable).
//  • 202 → el wait del backend venció (>240s): NO es error. Polleamos
//    `imagenes_generadas` por execution_id como respaldo — el backend escribe
//    la fila del video (incluidas las de error, cosa que n8n nunca hizo).

import { atlasBackendFetch } from './atlasBackend';
import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { GenerateAdError, mapErrorTypeToMessage } from './generateAdRequest';
import { pollGeneratedByExecutionId } from '../pages/v2/pollGeneratedByExecutionId';

const N8N_SINGLE_SCENE_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/64e992a5-c15e-4744-aeb6-dfa530700b86';
const N8N_MULTI_SCENE_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/video_cinema';
const N8N_VIDEO_PROMPT_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_prompt_video';
const N8N_VIDEO_TEMPLATE_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/generar_video_plantilla';

// Un video tarda varios minutos — mismo orden que el poll de video del
// asistente (VIDEO_POLL_TIMEOUT_MS de agentGenerate).
const VIDEO_ROW_POLL_TIMEOUT_MS = 15 * 60 * 1000;

// POST a un endpoint de VIDEO del atlas-backend → id de la fila del video.
// Común a from-image y from-template: saca user_id, agrega execution_id y
// maneja 402 (billing) / 202 (wait vencido → poll de respaldo por
// execution_id; la fila llega con tipo 'video' o con has_error) / 200
// (video_id real) / errores tipados.
async function atlasVideoRequest(
  path: string,
  body: Record<string, unknown>,
  companyId: string,
): Promise<string> {
  const { user_id: _userId, ...atlasBody } = body;
  void _userId;
  const executionId = crypto.randomUUID();
  atlasBody.execution_id = executionId;

  let res: Response;
  try {
    res = await atlasBackendFetch(path, atlasBody);
  } catch (e: any) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      e?.message ?? 'network_error',
    );
  }
  const data: any = await res.json().catch(() => null);

  if (res.status === 402) {
    throw new GenerateAdError(
      'billing',
      mapErrorTypeToMessage('billing'),
      data?.error_message ?? 'billing',
    );
  }

  if (res.status === 202) {
    const rows = await pollGeneratedByExecutionId({
      executionId,
      companyId,
      expectedCount: 1,
      timeoutMs: VIDEO_ROW_POLL_TIMEOUT_MS,
    });
    const ok = rows.find((r) => !r.has_error);
    if (ok) return ok.id;
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      rows.length > 0 ? 'video_row_has_error' : 'video_row_timeout',
    );
  }

  if (!res.ok || data == null) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      data ? JSON.stringify(data) : `HTTP ${res.status}`,
    );
  }

  if (data?.response === 'error' && data?.error_type) {
    const t = data.error_type as Parameters<typeof mapErrorTypeToMessage>[0];
    throw new GenerateAdError(t, mapErrorTypeToMessage(t), data?.error_message ?? null);
  }

  const videoId =
    data?.response === 'success' && (data?.video_id || data?.video_generado_id)
      ? String(data.video_id || data.video_generado_id)
      : null;
  if (!videoId) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      'unexpected_response',
    );
  }
  return videoId;
}

/** Prompt sugerido para animar una imagen generada. Lanza Error con mensaje
 *  mostrable si falla (los callers ya muestran `e.message`). */
export async function fetchSuggestedVideoPrompt(
  imageId: string | number,
  /** Solo para el fallback n8n (el backend nuevo usa el JWT). */
  userId: string,
): Promise<string> {
  let res: Response;
  if (USE_ATLAS_BACKEND) {
    res = await atlasBackendFetch('/v1/videos/prompt', { image_id: imageId });
  } else {
    res = await fetch(N8N_VIDEO_PROMPT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify({ user_id: userId, image_id: imageId }),
    });
  }
  if (!res.ok) throw new Error('Error al generar la descripción');
  const data: any = await res.json().catch(() => null);
  return ((data?.response || data?.prompt || '') as string).toString().trim();
}

/**
 * Genera el video de "Animar imagen" (ambas ramas del toggle multi-escena) y
 * devuelve el `video_id`. `body` es el payload actual de los callers (con
 * `user_id`, `scenes`, `multiples_escenas`, etc.); acá se decide el destino
 * según el flag. En atlas, si el wait vence (202 — normal con Kling
 * multi-shot, 3-8 min) se pollea la fila del video por execution_id y se
 * devuelve su id real.
 *
 * Lanza `GenerateAdError` clasificado (billing accionable / technical) — los
 * callers muestran `e.message` si `e.isActionable`, o su copy genérico.
 */
export async function generateVideoFromImage(
  body: Record<string, unknown>,
  opts: { companyId: string },
): Promise<string> {
  if (!USE_ATLAS_BACKEND) {
    // Rollback n8n: misma bifurcación de webhooks que tenían los callers.
    const n8nUrl =
      body.multiples_escenas === true
        ? N8N_MULTI_SCENE_URL
        : N8N_SINGLE_SCENE_URL;
    const res = await fetch(n8nUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new GenerateAdError(
        'technical',
        mapErrorTypeToMessage('technical'),
        `HTTP ${res.status}`,
      );
    }
    const data: any = await res.json().catch(() => null);
    const videoId =
      data?.response === 'success' && (data?.video_id || data?.video_generado_id)
        ? String(data.video_id || data.video_generado_id)
        : null;
    if (!videoId) {
      throw new GenerateAdError(
        'technical',
        mapErrorTypeToMessage('technical'),
        'unexpected_response',
      );
    }
    return videoId;
  }

  return atlasVideoRequest('/v1/videos/from-image', body, opts.companyId);
}

/**
 * Video desde PLANTILLA (Kling, 3-10 min de generación). Con atlas lo normal
 * es recibir 202 y cerrar por el poll de execution_id — el caller recibe el
 * id REAL de la fila del video (adiós lookup por created_at). Con flag OFF va
 * al webhook n8n `generar_video_plantilla`, que NUNCA existió (404 — la
 * feature estaba rota); queda solo como rollback literal del flag.
 */
export async function generateVideoFromTemplate(
  body: Record<string, unknown>,
  opts: { companyId: string },
): Promise<string> {
  if (!USE_ATLAS_BACKEND) {
    const res = await fetch(N8N_VIDEO_TEMPLATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new GenerateAdError(
        'technical',
        mapErrorTypeToMessage('technical'),
        `HTTP ${res.status}`,
      );
    }
    const data: any = await res.json().catch(() => null);
    const videoId =
      data?.response === 'success' && (data?.video_id || data?.video_generado_id)
        ? String(data.video_id || data.video_generado_id)
        : null;
    if (!videoId) {
      throw new GenerateAdError(
        'technical',
        mapErrorTypeToMessage('technical'),
        'unexpected_response',
      );
    }
    return videoId;
  }
  return atlasVideoRequest('/v1/videos/from-template', body, opts.companyId);
}
